import { getInquiries, type InquiryCategory, type InquiryProduct, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function InquiryListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [product, setProduct] = useState<InquiryProduct>();
  const [category, setCategory] = useState<InquiryCategory>();
  const [status, setStatus] = useState<string>();

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.inquiries.adminList(page, limit, status, product, category),
    queryFn: () => getInquiries({ page, limit, status, product, category }),
  });

  const inquiries = isError ? [] : data?.list;
  const total = isError ? 0 : data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <TitleContainer title="문의사항 관리">
      <Group mb="md">
        <Select
          label="문의 제품"
          value={product ?? "ALL"}
          allowDeselect={false}
          data={[
            { value: "ALL", label: "전체 제품" },
            { value: "ENTERPRISE", label: "Enterprise" },
            { value: "PRO", label: "Pro" },
          ]}
          onChange={(value) => {
            setProduct(value === "ALL" ? undefined : (value as InquiryProduct));
            setCategory(undefined);
            setPage(1);
          }}
        />
        <Select
          label="문의 유형"
          value={category ?? "ALL"}
          allowDeselect={false}
          data={[
            { value: "ALL", label: "전체 유형" },
            { value: "GENERAL", label: "사용법·기타" },
            { value: "GENERATION", label: "생성 실패" },
            { value: "BILLING", label: "결제·환불" },
            { value: "ACCOUNT", label: "계정" },
          ]}
          onChange={(value) => {
            setCategory(value === "ALL" ? undefined : (value as InquiryCategory));
            setPage(1);
          }}
        />
        <Select
          label="문의 상태"
          value={status ?? "ALL"}
          allowDeselect={false}
          data={[
            { value: "ALL", label: "전체 상태" },
            { value: "OPEN", label: "답변 대기" },
            { value: "ANSWERED", label: "답변 완료" },
          ]}
          onChange={(value) => {
            setStatus(value === "ALL" ? undefined : (value ?? undefined));
            setPage(1);
          }}
        />
      </Group>
      {isError && (
        <Alert color="red" mb="md" title="문의 목록 조회 실패">
          목록을 확인하지 못했습니다. 이전 목록을 숨겼습니다.
          <Button variant="subtle" disabled={isFetching} onClick={() => void refetch()}>
            문의 목록 다시 조회
          </Button>
        </Alert>
      )}
      <Card withBorder radius="md" p={0} pos="relative" style={{ overflow: "hidden" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />
        <Table.ScrollContainer minWidth={800}>
          <Table highlightOnHover withTableBorder verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>상태</Table.Th>
                <Table.Th>제품</Table.Th>
                <Table.Th>유형</Table.Th>
                <Table.Th>제목</Table.Th>
                <Table.Th>작성자</Table.Th>
                <Table.Th>작성일</Table.Th>
                <Table.Th>관리</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={7} ta="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        문의사항 목록을 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : !inquiries?.length ? (
                <Table.Tr>
                  <Table.Td colSpan={7} ta="center" py="xl">
                    <Text c="dimmed">
                      {isError ? "문의 목록을 확인할 수 없습니다." : "문의사항이 없습니다."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                inquiries.map((inquiry) => (
                  <Table.Tr key={inquiry.id}>
                    <Table.Td>
                      <Badge color={inquiry.status === "ANSWERED" ? "green" : "gray"}>
                        {inquiry.status === "ANSWERED" ? "답변완료" : "답변대기"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{inquiry.product === "PRO" ? "Pro" : "Enterprise"}</Table.Td>
                    <Table.Td>
                      {
                        {
                          GENERAL: "사용법·기타",
                          GENERATION: "생성 실패",
                          BILLING: "결제·환불",
                          ACCOUNT: "계정",
                        }[inquiry.category ?? "GENERAL"]
                      }
                    </Table.Td>
                    <Table.Td>{inquiry.title}</Table.Td>
                    <Table.Td>{inquiry.user?.name}</Table.Td>
                    <Table.Td>{dayjs(inquiry.createdAt).format("YYYY.MM.DD HH:mm")}</Table.Td>
                    <Table.Td>
                      <ActionIcon
                        aria-label="문의 상세 보기"
                        variant="light"
                        color="blue"
                        onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                      >
                        <ChevronRight size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Group justify="space-between" mt="lg">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              페이지당 표시:
            </Text>
            <Select
              value={String(limit)}
              onChange={(value) => {
                setLimit(Number(value));
                setPage(1); // Reset to first page when changing limit
              }}
              data={[
                { value: "10", label: "10개" },
                { value: "20", label: "20개" },
                { value: "50", label: "50개" },
                { value: "100", label: "100개" },
              ]}
              w={100}
            />
            <Text size="sm" c="dimmed">
              총 {total}개
            </Text>
          </Group>

          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </TitleContainer>
  );
}
